import { deepEquals, exists } from '@jack-henry/frontend-utils/functions';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SelectItemVm, TmFormControl, TmValueChanged } from './forms';
import './tm-currency-field';
import './tm-select';

export enum CurrencyRangeType {
    AllAmounts = 'All Amounts',
    SpecificAmount = 'Specific Amount',
    AmountRange = 'Amount Range',
    Between = 'Between',
    LessThan = 'Less Than',
    GreaterThan = 'Greater Than',
}

export interface CurrencyRange {
    rangeType: CurrencyRangeType;
    specificAmount?: number;
    floorAmount?: number;
    ceilAmount?: number;
}

export const tagName = 'tm-currency-range';
@customElement(tagName)
export class TmCurrencyRange extends TmFormControl<CurrencyRange> {
    @property({ type: String })
    public label = 'Amount';

    @property({ type: Array })
    public currencyRangeOptions: CurrencyRangeType[] = [
        CurrencyRangeType.AllAmounts,
        CurrencyRangeType.SpecificAmount,
        CurrencyRangeType.AmountRange,
    ];

    @property({ attribute: false })
    public initial: CurrencyRange = {
        rangeType: CurrencyRangeType.AllAmounts,
    };

    @state()
    private rangeType?: CurrencyRangeType;

    @state()
    private specificAmount?: number;

    @state()
    private floorAmount?: number;

    @state()
    private ceilAmount?: number;

    private get rangeOptionsForSelect(): SelectItemVm<string>[] {
        return this.currencyRangeOptions.map(rangeOption => ({
            label: rangeOption,
            value: rangeOption,
        }));
    }

    private get currencyRange(): CurrencyRange {
        const { rangeType, specificAmount, floorAmount, ceilAmount } = this;
        return {
            rangeType,
            specificAmount,
            floorAmount,
            ceilAmount,
        } as CurrencyRange;
    }

    private get isValidCurrencyRange() {
        if (!this.rangeType) {
            return false;
        }
        switch (this.rangeType) {
            case CurrencyRangeType.SpecificAmount:
                return exists(this.specificAmount) && this.specificAmount > 0;
            case CurrencyRangeType.AmountRange:
            case CurrencyRangeType.Between: {
                if (exists(this.floorAmount) && exists(this.ceilAmount)) {
                    return this.floorAmount <= this.ceilAmount && this.ceilAmount > 0;
                }

                return true;
            }
            default:
                return true;
        }
    }

    protected validate() {
        return this.isValidCurrencyRange ?? true;
    }

    protected updated(changedProperties: Map<PropertyKey, unknown>): void {
        const lastInitial = changedProperties.get('initial') as CurrencyRange | undefined;
        // If the range is updated, but has the same values as before then don't parse out the individual properties again.
        // Dispatches redundant @selection events
        if (
            changedProperties.has('initial') &&
            exists(this.initial) &&
            !deepEquals(lastInitial, this.initial)
        ) {
            this.unpackRange();
        }
    }

    protected render() {
        return [
            this.renderRangeTypeSelect(),
            this.renderSpecificAmountField(),
            this.renderRangedAmountFields(),
        ];
    }

    private unpackRange() {
        const { rangeType, specificAmount, floorAmount, ceilAmount } = this.initial;
        this.rangeType = rangeType;
        this.specificAmount = specificAmount;
        this.floorAmount = floorAmount;
        this.ceilAmount = ceilAmount;
    }

    private dispatchValueChange() {
        this.changeValue(this.currencyRange);
    }

    private updateAmounts() {
        if (!this.rangeType) {
            return;
        }

        this.specificAmount = undefined;
        this.floorAmount = undefined;
        this.ceilAmount = undefined;

        switch (this.rangeType as CurrencyRangeType) {
            case CurrencyRangeType.SpecificAmount:
            case CurrencyRangeType.LessThan:
            case CurrencyRangeType.GreaterThan:
                this.specificAmount = 0;
                break;
            case CurrencyRangeType.AmountRange:
            case CurrencyRangeType.Between:
                this.floorAmount = 0;
                this.ceilAmount = 0;
                break;
            default:
                break;
        }
    }

    private renderRangeTypeSelect() {
        return html` <tm-select
            .label=${this.label}
            .items=${this.rangeOptionsForSelect}
            .initial=${this.rangeType}
            @value-changed=${(e: TmValueChanged<CurrencyRangeType>) => {
                const { value } = e.detail;
                if (!value) {
                    return;
                }

                this.rangeType = value;
                if (!deepEquals(this.initial, this.currencyRange)) {
                    this.updateAmounts();
                }
                this.dispatchValueChange();
            }}
        ></tm-select>`;
    }

    private renderSpecificAmountField() {
        const showSpecificAmountField =
            this.rangeType === CurrencyRangeType.SpecificAmount ||
            this.rangeType === CurrencyRangeType.LessThan ||
            this.rangeType === CurrencyRangeType.GreaterThan;
        if (!showSpecificAmountField) {
            return nothing;
        }
        return html` <tm-currency-field
            .initial=${this.specificAmount ? this.specificAmount?.toFixed(2) : '0.00'}
            @value-changed=${(e: TmValueChanged<string>) => {
                this.handleCurrencyChange(e, 'specificAmount');
            }}
        ></tm-currency-field>`;
    }

    private renderFloorAmountField() {
        return html` <tm-currency-field
            .initial=${this.floorAmount ? this.floorAmount?.toFixed(2) : '0.00'}
            @value-changed=${(e: TmValueChanged<string>) => {
                this.handleCurrencyChange(e, 'floorAmount');
            }}
            .invalid=${!this.isValidCurrencyRange &&
            exists(this.floorAmount) &&
            this.floorAmount > 0}
            class="min-w-0 w-full"
        ></tm-currency-field>`;
    }

    private renderCeilAmountField() {
        return html` <tm-currency-field
            .initial=${this.ceilAmount ? this.ceilAmount?.toFixed(2) : '0.00'}
            @value-changed=${(e: TmValueChanged<string>) => {
                this.handleCurrencyChange(e, 'ceilAmount');
            }}
            .invalid=${!this.isValidCurrencyRange &&
            exists(this.floorAmount) &&
            this.floorAmount > 0}
            class="min-w-0 w-full"
        ></tm-currency-field>`;
    }

    private renderRangedAmountFields() {
        const showRangeFields =
            this.rangeType === CurrencyRangeType.AmountRange ||
            this.rangeType === CurrencyRangeType.Between;
        if (!showRangeFields) {
            return nothing;
        }
        return html` <div class="flex items-center">
            ${this.renderFloorAmountField()}
            <div class="px-2">-</div>
            ${this.renderCeilAmountField()}
        </div>`;
    }

    private handleCurrencyChange(
        e: TmValueChanged<string>,
        field: 'floorAmount' | 'ceilAmount' | 'specificAmount'
    ) {
        const { value } = e.detail;
        if (!value) {
            return;
        }
        const parsedValue = parseFloat(value);
        if (isNaN(parsedValue) || parsedValue === this[field]) {
            return;
        }
        this[field] = parsedValue;
        this.dispatchValueChange();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmCurrencyRange;
    }
}
